import React, { useState, useEffect, useRef } from "react";
import { IoIosArrowRoundForward } from "react-icons/io";
import { IoMdAdd } from "react-icons/io";
import { IoMdClose } from "react-icons/io";
import { CiWarning } from "react-icons/ci";
import { IoSend } from "react-icons/io5";
import { API_BASE_URL } from "../utils/apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../utils/formatNumber";

const BalancesSection = ({ dashboardData, availableBalance, walletData, initialCurrency, triggerPayout }) => {
  const [isBalanceDropdownOpen, setIsBalanceDropdownOpen] = useState(false);
  const [isManagePayoutsOpen, setIsManagePayoutsOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState("$244.56");
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [showBalanceTooltip, setShowBalanceTooltip] = useState(false);
  const tooltipRef = useRef(null);

  // Compute real withdrawable balance from on-chain wallet data
  const trc20Balance = walletData?.find(w => w.walletNetwork === "USDT-TRC20")?.balance || 0;
  const erc20Balance = walletData?.find(w => w.walletNetwork === "USDT-ERC20")?.balance || 0;
  const totalWithdrawable = trc20Balance + erc20Balance;

  // Payout modal states
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState("USDT-TRC20");
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAwaitingCode, setIsAwaitingCode] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [codeError, setCodeError] = useState("");

  // Real withdrawable balance for the selected currency (on-chain balance capped by platform balance)
  const currencyBalance = selectedCurrency === "USDT-TRC20" ? trc20Balance : erc20Balance;
  const realBalance = Math.min(availableBalance || 0, currencyBalance > 0 ? currencyBalance : availableBalance || 0);
  const { t } = useTranslation();
  // Validation error states
  const [walletAddressError, setWalletAddressError] = useState("");
  const [amountError, setAmountError] = useState("");
  const currencyDropdownRef = useRef(null);

  const currencyOptions = [
    {
      value: "USDT-TRC20",
      label: "USDT-TRC20",
      image: "/TRC20.png",
    },
    {
      value: "USDT-ERC20",
      label: "USDT-ERC20",
      image: "/USDT-ERC20.png",
    },
  ];

  const handleBalanceSelect = (balance) => {
    setSelectedBalance(balance.value);
    setIsBalanceDropdownOpen(false);
  };

  const handleManagePayoutsSelect = (option) => {
    setIsManagePayoutsOpen(false);
    // Handle manage payouts selection
  };

  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency.value);
    setIsCurrencyDropdownOpen(false);
  };

  const handlePasteAddress = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setWalletAddress(text);
    } catch (err) {
      console.error("Failed to paste:", err);
    }
  };

  const handleWithdrawal = async (codeParam) => {
    let data = null;
    try {
      setIsLoading(true);

      // Get the token from localStorag

      // Prepare the request body
      const requestBody = {
        currencyType: selectedCurrency,
        merchantWallet: walletAddress,
        amount: parseFloat(amount),
      };
      if (codeParam) {
        requestBody.twoFACode = codeParam;
        requestBody.otp = codeParam;
        requestBody.code = codeParam;
      }

      // Make the API call
      const response = await fetch(`${API_BASE_URL}/withdrawal`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Withdrawal failed");
      }

      // Handle second-factor requirement
      if (data?.data?.requires2FA || data?.data?.requiresOTP) {
        setIsAwaitingCode(true);
        setTwoFACode("");
        setCodeError("");
        toast.info(
          data.message || "Verification code sent. Enter the code to continue.",
          {
            position: "top-right",
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
        return;
      }

      // Show toast based on actual withdrawal status (API always returns HTTP 200)
      const withdrawalStatus = data?.data?.withdrawal?.status;
      if (withdrawalStatus === "failed") {
        toast.error(
          data.message ||
            t("recentDashboard.payoutModal.failedToProcessWithdrawal"),
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      } else {
        toast.success(
          data.message ||
            t("recentDashboard.payoutModal.withdrawalRequestSubmitted"),
          {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      }

      // Close modal after a delay
      setTimeout(() => {
        setIsPayoutModalOpen(false);
        setCurrentStep(1);
        setAmount("");
        setWalletAddress("");
        setWalletAddressError("");
        setAmountError("");
        setIsAwaitingCode(false);
        setTwoFACode("");
        setCodeError("");
      }, 2000);
    } catch (err) {
      // If backend signals code required via error response, surface code input
      if (
        data?.error === "2FA_CODE_REQUIRED" ||
        data?.error === "2FA_CODE_EXPIRED"
      ) {
        setIsAwaitingCode(true);
        setTwoFACode("");
        setCodeError("");
        toast.info(
          data?.message ||
            "2FA code is required to continue. Check your email/app.",
          {
            position: "top-right",
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
        setIsLoading(false);
        return;
      }

      // Show error toast
      toast.error(
        data?.message ||
          err.message ||
          t("recentDashboard.payoutModal.failedToProcessWithdrawal"),
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      console.error("Withdrawal error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Address validation helper
  const isValidWalletAddress = (address, currency) => {
    if (!address) return false;
    if (currency === "USDT-TRC20") {
      // TRC20 addresses start with T and are 34 chars
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
    } else if (currency === "USDT-ERC20") {
      // ERC20 addresses start with 0x and are 42 chars
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    }
    return false;
  };

  const handleContinue = () => {
    if (currentStep === 1) {
      // Validate wallet address
      if (!walletAddress.trim()) {
        setWalletAddressError(
          t("recentDashboard.payoutModal.walletAddressRequired"),
        );
        return;
      }
      if (!isValidWalletAddress(walletAddress.trim(), selectedCurrency)) {
        setWalletAddressError(
          selectedCurrency === "USDT-TRC20"
            ? "Invalid TRC20 address. Must start with 'T' and be 34 characters."
            : "Invalid ERC20 address. Must start with '0x' and be 42 characters.",
        );
        return;
      }
      setWalletAddressError("");
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate amount
      if (!amount.trim()) {
        setAmountError(t("recentDashboard.payoutModal.amountRequired"));
        return;
      }
      const reqAmount = parseFloat(amount);
      if (isNaN(reqAmount) || reqAmount <= 0) {
        setAmountError(
          t("recentDashboard.payoutModal.amountMustBeGreaterThanZero"),
        );
        return;
      }
      if (reqAmount > realBalance) {
        setAmountError(`Insufficient balance. Available: ${realBalance.toFixed(2)} USDT`);
        return;
      }
      setAmountError("");
      setCurrentStep(3);
    } else {
      // Handle final submission - Call withdrawal API
      if (isAwaitingCode) {
        if (!twoFACode.trim()) {
          setCodeError("Code is required");
          return;
        }
        setCodeError("");
        handleWithdrawal(twoFACode.trim());
      } else {
        handleWithdrawal();
      }
    }
  };

  const handleReturn = () => {
    if (currentStep === 1) {
      setIsPayoutModalOpen(false);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleMaxAmount = () => {
    setAmount(realBalance.toString());
  };

  const handleCloseModal = () => {
    setIsPayoutModalOpen(false);
    setCurrentStep(1);
    setAmount("");
    setWalletAddress("");
    setWalletAddressError("");
    setAmountError("");
    setIsAwaitingCode(false);
    setTwoFACode("");
    setCodeError("");
  };

  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target)
      ) {
        setIsCurrencyDropdownOpen(false);
      }
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target)
      ) {
        setShowBalanceTooltip(false);
      }
    };

    if (isCurrencyDropdownOpen || showBalanceTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCurrencyDropdownOpen, showBalanceTooltip]);

  // Coin card → payout modal bridge: when triggerPayout changes, open payout modal with preselected currency
  useEffect(() => {
    if (triggerPayout && triggerPayout > 0) {
      if (initialCurrency) {
        setSelectedCurrency(initialCurrency);
      }
      setIsPayoutModalOpen(true);
    }
  }, [triggerPayout]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isPayoutModalOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      // Prevent scrolling
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
  }, [isPayoutModalOpen]);

  return (
    <div className="bg-gray-100 py-3">
      <div className="mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 lg:mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {t("recentDashboard.Balances")}
          </h1>

          <div className="flex items-center space-x-2 lg:space-x-3">
            {/* Balance Dropdown */}
            <div className="relative">
              <button className="flex items-center justify-center space-x-1 bg-[#348BED1A] text-blue-700 px-4 py-2 rounded-[10px] w-36 lg:w-40">
                <div className="flex items-center justify-center">
                  <span className="text-xs lg:text-lg text-[#348BED] mr-1 mt-1">
                    $
                  </span>
                  <span className="font-semibold text-base lg:text-[25px] text-[#348BED]">
                    {formatNumber(totalWithdrawable, 2)}
                  </span>
                </div>
              </button>
            </div>

            {/* Info Icon with Tooltip */}
            <div className="relative" ref={tooltipRef}>
              <button
                onClick={() => setShowBalanceTooltip(!showBalanceTooltip)}
                className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors flex-shrink-0"
              >
                <svg className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              {showBalanceTooltip && (
                <div className="absolute top-full left-0 sm:left-auto sm:right-0 mt-2 w-72 sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-4">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">{t("recentDashboard.balanceTooltipTitle")}</p>
                      <p className="text-xs text-gray-600 leading-relaxed">{t("recentDashboard.balanceTooltipBody")}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 lg:gap-4">
          {/* Payout Button */}
          <button
            onClick={() => setIsPayoutModalOpen(true)}
            className="flex items-center space-x-1 lg:space-x-2 bg-white border border-gray-300 h-10 text-gray-700 px-3 lg:px-6 py-2 lg:py-1 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <IoIosArrowRoundForward size={29} />
            <span className="font-small text-sm lg:text-base">
              {" "}
              {t("recentDashboard.payout")}
            </span>
          </button>

          {/* Top Up Button — visual only, non-functional */}
          <button
            onClick={() => {}}
            className="flex items-center space-x-1 lg:space-x-2 bg-white border border-gray-300 h-10 text-gray-700 px-3 lg:px-6 py-2 lg:py-1 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <IoMdAdd />
            <span className="font-small text-sm lg:text-base">
              {t("recentDashboard.topUp")}
            </span>
          </button>

          {/* Manage Payouts Button */}
          <div className="relative">
            {/* <button
              onClick={() => setIsManagePayoutsOpen(!isManagePayoutsOpen)}
              className="flex items-center space-x-1 lg:space-x-2 bg-white border border-gray-300 h-10 text-gray-700 px-3 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span className="font-small text-sm lg:text-base">
                Manage payouts
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${isManagePayoutsOpen ? "rotate-180" : ""
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button> */}

            {/* Manage Payouts Dropdown Menu */}
            {/* {isManagePayoutsOpen && (
              <div className="absolute top-full left-0 mt-2 w-40 lg:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                {managePayoutsOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleManagePayoutsSelect(option)}
                    className="w-full flex items-center px-3 lg:px-4 py-2 lg:py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    <span className="text-gray-700 font-medium text-sm lg:text-base">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Payout Modal */}
      {isPayoutModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            // Only close if clicking directly on the backdrop, not on the modal content
            if (e.target === e.currentTarget) {
              // Optionally prevent closing on backdrop click
              // handleCloseModal();
            }
          }}
        >
          <div
            className="bg-gray-100 rounded-[19px] shadow-xl w-full max-w-[540px] h-auto max-h-[90vh] min-h-[500px] sm:h-[580px] flex flex-col mx-4 sm:mx-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative border-b border-gray-200">
              {/* Close Button in Top Right Corner */}
              <button
                onClick={handleCloseModal}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors text-2xl leading-none"
              >
                ×
              </button>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                {/* Your modal content goes here */}
                <h2 className="text-2xl sm:text-[28px] font-medium text-gray-900">
                  {t("recentDashboard.payoutModal.title")}
                </h2>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-4 sm:px-6 flex-1 flex flex-col overflow-y-auto sm:overflow-y-visible">
              {/* Instructional Text */}
              <p className="text-gray-700 mb-3 sm:mb-4 mt-2 sm:mt-4 text-sm sm:text-base">
                {t("recentDashboard.payoutModal.selectWhereToPayout")}{" "}
                <span className="text-[#348BED] cursor-pointer">
                  {" "}
                  {t("recentDashboard.payoutModal.stablecoins")}.
                </span>
              </p>

              {/* Step 1: Wallet & Currency Selection */}
              {currentStep === 1 && (
                <>
                  {/* Type of Currency Section */}
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      {t("recentDashboard.payoutModal.typeOfCurrency")}
                    </label>
                    <div className="relative" ref={currencyDropdownRef}>
                      <button
                        onClick={() =>
                          setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)
                        }
                        className="w-full flex items-center justify-between px-4 py-2 bg-white rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          {/* Selected Currency Image */}
                          <div className="w-8 h-8 rounded-full  flex items-center justify-center bg-gray-100">
                            <img
                              src={
                                currencyOptions.find(
                                  (c) => c.value === selectedCurrency,
                                )?.image || "/TRC20.png"
                              }
                              alt={selectedCurrency}
                              className="w-8 h-8 object-cover"
                            />
                          </div>
                          <span className="text-gray-900 font-medium">
                            {selectedCurrency}
                          </span>
                        </div>

                        <svg
                          className={`w-5 h-5 text-gray-500 transition-transform ${
                            isCurrencyDropdownOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>

                      {/* Currency Dropdown */}
                      {isCurrencyDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-60 overflow-y-auto">
                          {currencyOptions.map((currency) => (
                            <button
                              key={currency.value}
                              onClick={() => handleCurrencySelect(currency)}
                              className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                            >
                              <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                                <img
                                  src={currency.image}
                                  alt={currency.label}
                                  className="w-8 h-8 object-cover"
                                />
                              </div>
                              <span className="text-gray-900 font-medium">
                                {currency.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Wallet Address Section */}
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      {t("recentDashboard.payoutModal.walletAddress")}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={walletAddress}
                        onChange={(e) => {
                          setWalletAddress(e.target.value);
                          if (walletAddressError) setWalletAddressError("");
                        }}
                        placeholder={t(
                          "recentDashboard.payoutModal.enterAddress",
                        )}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-14 sm:pr-16 bg-white rounded-xl focus:outline-none text-sm sm:text-base ${
                          walletAddressError ? "border-2 border-red-500" : ""
                        }`}
                      />
                      <button
                        onClick={handlePasteAddress}
                        className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-[#348BED] hover:text-blue-700 font-medium transition-colors text-xs sm:text-sm"
                      >
                        {t("recentDashboard.payoutModal.paste")}
                      </button>
                    </div>
                    {walletAddressError && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {walletAddressError}
                      </p>
                    )}
                  </div>

                  {/* Spacer */}
                  {/* <div className="h-24"></div> */}
                </>
              )}

              {/* Step 2: Amount Selection */}
              {currentStep === 2 && (
                <div className="mb-4 sm:mb-6">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    {t("recentDashboard.payoutModal.amount")}
                  </label>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers, max 8 digits
                      if (/^\d{0,8}$/.test(value)) {
                        setAmount(value);
                        if (amountError) setAmountError("");
                      }
                    }}
                    placeholder="Enter amount"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white rounded-xl focus:outline-none text-sm sm:text-base ${
                      amountError
                        ? "border-2 border-red-500"
                        : "focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  />
                  {amountError && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">
                      {amountError}
                    </p>
                  )}

                  {/* <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Balance: {balance} USDT</span>
                    <button
                      onClick={handleMaxAmount}
                      className="text-sm text-[#348BED] hover:text-blue-700 font-medium transition-colors"
                    >
                      Max
                    </button>
                  </div> */}
                </div>
              )}

              {/* Step 3: Preview Details */}
              {currentStep === 3 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-3 sm:mb-4">
                    {" "}
                    {t("recentDashboard.payoutModal.previewDetails")}
                  </h3>

                  {/* Preview Details List */}
                  <div className="space-y-3 sm:space-y-4 bg-white p-3 sm:p-4 rounded-xl">
                    {/* Currency Type */}
                    <div className="flex items-center justify-between pb-2 sm:pb-3">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("recentDashboard.payoutModal.currencyType")}:
                      </span>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
                          <img
                            src={
                              currencyOptions.find(
                                (c) => c.value === selectedCurrency,
                              )?.image || "/TRC20.png"
                            }
                            alt={selectedCurrency}
                            className="w-6 h-6 sm:w-8 sm:h-8 object-cover"
                          />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {selectedCurrency}
                        </span>
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex items-center justify-between pb-2 sm:pb-3">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("recentDashboard.payoutModal.amount")} :
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-900">
                        {amount || "0"} USDT
                      </span>
                    </div>

                    {/* Wallet Address */}
                    <div className="flex items-center justify-between pb-2 sm:pb-3">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("recentDashboard.payoutModal.walletAddress")} :
                      </span>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900 max-w-[120px] sm:max-w-[200px] truncate">
                          {walletAddress || "No address entered"}
                        </span>
                        {walletAddress && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(walletAddress);
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <svg
                              className="w-4 h-4 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Fee */}
                    {/* <div className="flex items-center justify-between pb-1">
                      <span className="text-sm text-gray-600">Fee:</span>
                      <span className="text-sm font-medium text-gray-900">1 USDT</span>
                    </div> */}
                  </div>

                  {isAwaitingCode && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-2 mt-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Enter verification code
                      </label>
                      <input
                        type="text"
                        value={twoFACode}
                        onChange={(e) => {
                          setTwoFACode(e.target.value);
                          setCodeError("");
                        }}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        disabled={isLoading}
                      />
                      {codeError && (
                        <p className="text-red-500 text-sm">{codeError}</p>
                      )}
                      {/* <p className="text-gray-500 text-sm">
                        A code was sent to your email to authorize this payout.
                      </p> */}
                    </div>
                  )}
                </div>
              )}

              {/* Warning Message - Only show in step 1 */}
              {currentStep === 1 && (
                <div className="bg-[#FFF3E0] rounded-[15px] p-4">
                  <div className="flex items-start space-x-3">
                    <CiWarning
                      className="text-[#FF9800] flex-shrink-0 mt-0.5"
                      size={22}
                    />
                    <p className="text-sm text-[#E78D06] leading-relaxed">
                      When payments are completed, you are withdrawing your
                      stablecoins from your wallet to our wallet. Payouts are
                      irreversible.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Progress Indicator & Buttons */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 mt-auto">
              {/* Progress Indicator */}
              <div className="flex items-center justify-center mb-3 sm:mb-4">
                <div className="flex items-center gap-1.5 sm:gap-2 bg-white p-2 rounded-[15px]">
                  <div
                    className={`w-8 sm:w-8 h-1.5 rounded-full ${currentStep >= 1 ? "bg-[#348BED]" : "bg-gray-300"}`}
                  ></div>
                  <div
                    className={`w-8 sm:w-8 h-1.5 rounded-full ${currentStep >= 2 ? "bg-[#348BED]" : "bg-gray-300"}`}
                  ></div>
                  <div
                    className={`w-8 sm:w-8 h-1.5 rounded-full ${currentStep >= 3 ? "bg-[#348BED]" : "bg-gray-300"}`}
                  ></div>
                </div>
                <span className="ml-2 bg-white sm:ml-3 text-xs sm:text-sm text-gray-600 font-medium px-2 rounded-[15px]">
                  {currentStep}/3
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <button
                  onClick={handleReturn}
                  disabled={isLoading}
                  className="flex-1 px-4 sm:px-6 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Return
                </button>

                <button
                  onClick={handleContinue}
                  disabled={isLoading}
                  className="flex-1 px-4 sm:px-6 py-2.5 bg-gradient-to-b from-[#3A5AD1] to-[#31ABFF] text-white rounded-lg hover:opacity-90 transition-colors font-medium text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : currentStep === 3 ? (
                    <>
                      Send
                      <IoSend />
                    </>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default BalancesSection;
